import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { AccountService, ProfileModel } from "@app/core";
import { PAGE_ROUTES } from "@app/pages/pages-routes";
import { PosConnectionStatusRefreshService, PosSettingsService } from "@app/services";
import { Unsubscriber } from "@app/shared/components";
import { AccountPosSystemSettingsConfiguration } from "clearline-api";
import { isIframe, UserPermissionService } from "clearline-common";
import { RoleTypes } from "core-kit";
import { filter } from "rxjs/operators";
import { EventService } from "../core/services/event.service";

export const LAYOUT_VERTICAL = "vertical";
export const LAYOUT_HORIZONTAL = "horizontal";

@Component({
  selector: "app-layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.scss"]
  // todo: add OnPush ChangeDetectionStrategy
})
export class LayoutComponent extends Unsubscriber implements OnInit, AfterViewInit {
  private layoutType: string;
  private currentUser: ProfileModel | null = null;
  /** True while a full-screen onboarding route is active (partner onboarding / quick setup). */
  private fullscreenRoute = false;

  isLoad = false;
  isMenuVisible = false;

  /**
   * URL prefixes that render full-screen without the app chrome (topbar/sidebar/footer).
   * Only partner onboarding: quick-setup keeps its existing profile-flag behaviour because
   * already-configured users are also routed into /quick-setup-integration.
   */
  private static readonly FULLSCREEN_ROUTES = [`/${PAGE_ROUTES.PARTNER_ONBOARDING}`];

  constructor(
    private eventService: EventService,
    private accountService: AccountService,
    private permissionService: UserPermissionService,
    private posSettingsService: PosSettingsService,
    private posConnectionStatusRefreshService: PosConnectionStatusRefreshService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    super();

    // Resolve the full-screen state from the URL synchronously first (the profile arrives
    // async, so relying on it alone flashes the vertical sidebar before onboarding takes
    // over). Must run before the userChanged$ subscription, which emits null synchronously.
    this.fullscreenRoute = this.isFullscreenUrl(this.router.url);
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationStart | NavigationEnd => e instanceof NavigationStart || e instanceof NavigationEnd))
      .subscribe((e) => {
        this.fullscreenRoute = this.isFullscreenUrl(e.url);
        this.updateLayout();
      });

    this.sub = this.accountService.userChanged$.subscribe((user) => {
      this.currentUser = user;
      this.updateLayout();
    });

    this.sub = this.posSettingsService.settings$.pipe(filter((v) => !!v)).subscribe((settings: AccountPosSystemSettingsConfiguration) => {
      this.permissionService.updatePermissionsByPosSettingsUpdates(settings);
    });

    this.sub = this.posConnectionStatusRefreshService.watch().subscribe();
  }

  private updateLayout(): void {
    this.isMenuVisible = this.visibleMenu(this.currentUser);

    if (isIframe() && this.isMenuVisible) {
      this.layoutType = LAYOUT_HORIZONTAL;
    } else {
      this.layoutType = this.isMenuVisible ? LAYOUT_VERTICAL : LAYOUT_HORIZONTAL;
    }
  }

  private isFullscreenUrl(url: string): boolean {
    return !!url && LayoutComponent.FULLSCREEN_ROUTES.some((route) => url.startsWith(route));
  }

  visibleMenu(user: ProfileModel) {
    if (this.fullscreenRoute) {
      return false;
    }
    const isOnboarding = !!user && !user.doneQuickSetup && (user.role === RoleTypes.Manager || user.role === RoleTypes.PartnerAdmin);
    return !isOnboarding;
  }

  ngOnInit() {
    this.sub = this.eventService.subscribe("changeLayout", (layout) => {
      this.layoutType = layout;
    });
  }

  ngAfterViewInit() {
    this.isLoad = true;

    this.cdr.detectChanges();
  }

  /**
   * Check if the vertical layout is requested
   */
  isVerticalLayoutRequested() {
    return this.layoutType === LAYOUT_VERTICAL;
  }

  /**
   * Check if the horizontal layout is requested
   */
  isHorizontalLayoutRequested() {
    return this.layoutType === LAYOUT_HORIZONTAL;
  }
}
